import axios, { AxiosRequestConfig } from 'axios';

import { apiRequest, apiRequests } from 'helpers';
import {
  OrganizationVerboseProps,
  OrganizationProps,
  OrganizationFullProps,
} from 'types';

// 404: "Organization id {organization_id} not found."
// 403: "User not allowed to access this organization."
export const fetchOrganizationById = (org_id: number) =>
  apiRequest<OrganizationVerboseProps>({
    method: 'GET',
    path: `/v3/organizations/${org_id}`,
  });

export const fetchOrganizations = (config: AxiosRequestConfig) => {
  return apiRequest<Array<OrganizationFullProps>>({
    method: 'GET',
    path: '/v3/organizations',
    ...config,
  });
};

export const fetchAbridgedOrganizations = (config: AxiosRequestConfig) => {
  return apiRequest<Array<OrganizationProps>>({
    method: 'GET',
    path: '/v3/organizations/abridged',
    ...config,
  });
};

// 404: "Organization not found."
// 409: "Existing organization with that id exists"
export const patchOrganization = (
  org_id: number,
  data: Partial<OrganizationVerboseProps>,
) => {
  return apiRequest<OrganizationVerboseProps>({
    method: 'PATCH',
    path: `/v3/organizations/${org_id}`,
    data: data,
  });
};

// 201: CREATED
// 404: "Parent organization not found."
// 404: "Child organization {child_organization_id} not found."
// 409: "Cannot make organization {child_organization_id} a child as it has users associated with it. Remove these users and try again."
// 409: "Cannot add children to an organization which is a child of another organization. Target is a child of {target_org.parent.id}: {target_org.parent.name}."
// 409: "Cannot make a parent organization a child of another org. This organization has {len(target_child.children)} children."
export const addOrganizations = (props: {
  org_id: number;
  child_ids: Array<number>;
}): Promise<boolean> => {
  const { org_id, child_ids } = props;
  return apiRequests(
    child_ids.map((child_id) => {
      return {
        method: 'POST',
        path: `/v3/organizations/${org_id}/children/${child_id}`,
      };
    }),
  ).then(
    axios.spread((...responces) => {
      const success =
        !!responces.length &&
        responces.every((responce) => responce.status === 201);
      return success;
    }),
  );
};

// 204: NO CONTENT
// 404: "Parent organization not found."
// 404: "Child organization {child_organization_id} not found."
export const removeOrganizations = (props: {
  org_id: number;
  child_ids: Array<number>;
}): Promise<boolean> => {
  const { org_id, child_ids } = props;
  return apiRequests(
    child_ids.map((child_id) => {
      return {
        method: 'DELETE',
        path: `/v3/organizations/${org_id}/children/${child_id}`,
      };
    }),
  ).then(
    axios.spread((...responces) => {
      const success =
        !!responces.length &&
        responces.every((responce) => responce.status === 204);
      return success;
    }),
  );
};

export const addCandidateToOrganization = (props: {
  candidateId: number;
  orgId: number;
}) => {
  const { candidateId, orgId } = props;
  return apiRequest({
    method: 'POST',
    path: `/v3/organizations/${orgId}/candidates/${candidateId}`,
  });
};
